<template>
  <div class="hero-slide">
    <template v-if="simple.length == 0 || detail.length == 0">
      <StartpageHeroTemplateBanner :entries="entries" />
    </template>
    <template v-else-if="simple.length == 1 && detail.length == 1">
      <div class="grid grid-cols-2 gap-6">
        <StartpageHeroElementTileSimple :entry="simple[0]" />
        <StartpageHeroElementTileDetailed :entry="detail[0]" />
      </div>
    </template>
    <template v-else-if="simple.length >= 1 && 1 <= detail.length">
      <div class="grid grid-cols-2 gap-8">
        <div>
          <vueper-slides
            ref="sliderSimple"
            class="no-shadow w-full design-v2"
            fixed-height="500px"
            :autoplay="true"
            gap="4"
            :bullets="false"
            @ready="readyHandlerSimple"
          >
            <vueper-slide v-for="entry in simple" :key="entry.priority">
              <template #content>
                <StartpageHeroElementTileSimple :entry="entry" />
              </template>
            </vueper-slide>
          </vueper-slides>
        </div>
        <div>
          <vueper-slides
            ref="sliderDetail"
            class="no-shadow w-full design-v2"
            fixed-height="500px"
            gap="4"
            :bullets="false"
            @ready="readyHandlerDetail"
          >
            <vueper-slide v-for="entry in detail" :key="entry.priority">
              <template #content>
                <StartpageHeroElementTileDetailed :entry="entry" :output-restricted="false" />
              </template>
            </vueper-slide>
          </vueper-slides>
        </div>
      </div>
    </template>
  </div>
</template>
<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';
import { useMutationObserver } from "@vueuse/core";
import type { SliderEntry } from '../SliderStructure';

const props = defineProps<{
  entries: SliderEntry[];
}>();

// Keep the existing Balanced image mapping, without a post-mount layout switch.
const tiles = computed(() => {
  const simple = [];
  const detail = [];

  for (const entry of props.entries ?? []) {
    if (entry.type === 'Secondary' && entry.images.primary) {
      detail.push({
        ...entry,
        image: entry.images.primary.sm,
        altText: entry.altText || entry.description || '',
        headlineOrange: false,
      });
    } else if (entry.images.secondary) {
      simple.push({
        ...entry,
        image: entry.images.secondary.lg,
        altText: entry.altText || entry.description || '',
      });
    }
  }

  return { simple, detail };
});

const simple = computed(() => tiles.value.simple);
const detail = computed(() => tiles.value.detail);


const sliderSimple = useTemplateRef('sliderSimple');
const sliderDetail = useTemplateRef('sliderDetail');

useMutationObserver(sliderSimple, (mutations) => {
  for (const mutation of mutations) {
    if (mutation.type === 'attributes') {
      setTabIndex(mutation.target, mutation.oldValue === 'true' ? 0 : -1);
    }
  }
}, {
  attributes: true,
  attributeFilter: ['aria-hidden'],
  attributeOldValue: true,
  subtree: true,
});
useMutationObserver(sliderDetail, (mutations) => {
  for (const mutation of mutations) {
    if (mutation.type === 'attributes') {
      setTabIndex(mutation.target, mutation.oldValue === 'true' ? 0 : -1);
    }
  }
}, {
  attributes: true,
  attributeFilter: ['aria-hidden'],
  attributeOldValue: true,
  subtree: true,
});

const readyHandlerSimple = () => {
  const sliderInnerTrack = sliderSimple.value?.container?.querySelector('.vueperslides__track-inner');
  const slideElements = sliderInnerTrack?.querySelectorAll('.vueperslide:not(.vueperslide--visible)') ?? [];

  for (const slideElement of slideElements) {
    setTabIndex(slideElement, -1);
  }

  useMutationObserver(sliderInnerTrack, (mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          setTabIndex(node, -1);
        }
      }
    }
  }, {
    childList: true,
  });
};

const readyHandlerDetail = () => {
  const sliderInnerTrack = sliderDetail.value?.container?.querySelector('.vueperslides__track-inner');
  const slideElements = sliderInnerTrack?.querySelectorAll('.vueperslide:not(.vueperslide--visible)') ?? [];

  for (const slideElement of slideElements) {
    setTabIndex(slideElement, -1);
  }

  useMutationObserver(sliderInnerTrack, (mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          setTabIndex(node, -1);
        }
      }
    }
  }, {
    childList: true,
  });
};

const setTabIndex = (element: Node, tabIndex: number): void => {
  element.tabIndex = tabIndex;
  const children = element.querySelectorAll('a');
  for (const child of children) {
    child.tabIndex = tabIndex;
  }
};

</script>
<style lang="postcss">
.hero-slide {
  & .vueperslides__arrow {
    @apply bg-white rounded-full shadow-lg;
    & svg {
      @apply w-6 h-6 text-primary py-1.5 px-1 scale-x-125 stroke-[3px];
    }
    &.vueperslides__arrow--prev {
      @apply left-0 -translate-x-1/2 -translate-y-1/2;
    }
    &.vueperslides__arrow--next {
      @apply right-0 translate-x-1/2 -translate-y-1/2;
    }
  }
  & .line-bullets {
    & .vueperslides__bullet {
      @apply mx-1;
      & .default {
        @apply !h-1 w-5 border border-secondary-dark shadow-none bg-secondary-dark;
      }
      &.vueperslides__bullet--active .default {
        @apply !h-1 w-10 border-2 border-primary shadow-none bg-primary;
      }
    }
  }
  .entry {
    @apply rounded-3xl overflow-hidden flex items-center lg:h-[500px];
  }
}
</style>
